import type { AxiosResponse } from "axios";
import { byIdsResultJSONZ, ByIdsResultJSON, UnavailableReason } from "_shared/_api/by-ids/by-ids-result";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import { reportSchemaFailure } from "_shared/_errors/client-error-reporter";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import DocumentsFormAPIRequest, { BulkDocumentRequest } from "documents/_api/commands/documents-form-api-request";
import UpdateDocumentAPIRequest from "documents/_api/commands/update-document-api-request";
import DocumentQuery from "documents/_api/queries/document-query";
import DocumentTopic from "documents/topics/document-topic";
import DocumentSchema, { DocumentJSON } from "documents/_schema/document-schema";
import Document from "documents/document";
import DocumentDownloadVerdict from "documents/downloads/document-download-verdict";
import { sessionPermissionTag } from "permissions/_api/permissions-api-service";
import UnavailableDocumentSchema, {
  UnavailableDocumentJSON,
} from "documents/unavailable-documents/_schema/unavailable-document-schema";
import UnavailableDocument from "documents/unavailable-documents/unavailable-document";
import { validateDocumentUploadRequests } from "documents/_api/document-upload-validation";
import { readDownloadErrorBody } from "documents/_api/document-download-failure";
import { DocumentSubjectMatter } from "permissions/resources/documents/document-subject-matter";
import { listCountsHook, listCountsTags } from "_shared/_api/list-counts/list-counts-hook";
import {
  DocumentListCountsJSONZ,
  type DocumentListCounts,
} from "documents/_api/queries/_schemas/document-list-counts-schema";
import z from "zod";

export type DocumentApiResult = Document | UnavailableDocument;

export type DocumentPermissionTarget = {
  id: string;
  name: string;
};

type DocumentApiResultJSON = DocumentJSON | UnavailableDocumentJSON;

type DocumentListArgs = {
  query: DocumentQuery;
  interfaceName?: string;
};

type DocumentIdArgs = {
  documentId: string;
};

type DocumentsByIdsArgs = {
  documentIds: string[];
};

const DocumentApiResultJSONZ = z.union([
  DocumentSchema.DocumentJSONZ,
  UnavailableDocumentSchema.UnavailableDocumentJSONZ,
]);

const DocumentPermissionTargetJSONZ = z.object({ id: z.string().uuid(), name: z.string() });

export const documentListTag = { type: "Document" as const, id: "LIST" };

/**
 * What a document the API did not return says in its place, by the reason the API gave (#6122).
 */
export const unavailableDocumentIssue: Record<UnavailableReason, string> = {
  missing: "Document no longer exists",
  unauthorized: "No access to this document",
};

/**
 * What became of a chat attachment saved as a document (#6053): added, already saved with these
 * topics, refused because another document carrying them has the same content (#6098), or refused
 * for its type. A refusal says why in `issue`.
 */
export const DocumentCopyResultJSONZ = z.object({
  fileName: z.string(),
  status: z.enum(["added", "already_copied", "duplicate", "refused"]),
  document: DocumentSchema.DocumentJSONZ.nullish(),
  existingDocumentId: z.uuid().nullish(),
  code: z.string().nullish(),
  issue: z.string().nullish(),
});

export type DocumentCopyResultJSON = z.infer<typeof DocumentCopyResultJSONZ>;

type OverrideClassificationArgs = {
  documentId: string;
  labels: DocumentSubjectMatter[];
  contentHash: string;
  reason: string;
};

type CopyAttachmentArgs = {
  forumId: string;
  fileId: string;
  topics: DocumentTopic[];
};

function deserializeDocumentApiResult(documentJSON: DocumentApiResultJSON): DocumentApiResult {
  if ("issue" in documentJSON && documentJSON.issue.trim().length > 0) {
    return UnavailableDocument.fromJSON(documentJSON);
  }

  return Document.fromJSON(documentJSON);
}

export const unreadableUploadAnswerIssue =
  "The answer for this file could not be read. Refresh to see whether it was added.";

/**
 * The server's answer for one uploaded file. An answer that does not parse is reported, and stands
 * in as a refusal of that file alone. The file may still have been added, so it says to look.
 */
function readUploadAnswer(row: unknown, request: BulkDocumentRequest): DocumentApiResult {
  const parsed = DocumentApiResultJSONZ.safeParse(row);
  if (parsed.success) return deserializeDocumentApiResult(parsed.data);

  reportSchemaFailure({
    schemaName: "DocumentApiResult",
    operation: "createDocuments",
    phase: "decode",
    fields: parsed.error.issues.map((issue) => issue.path.map(String).join(".")),
  });
  return UnavailableDocument.create({
    id: request.requestId,
    status: "refused",
    code: "upload_failed",
    issue: unreadableUploadAnswerIssue,
  });
}

/**
 * A document read says what this reader may do with the content (`contentAccess`), and that turns on
 * the reader's own grants. So every such read is read again when those grants change - an approved
 * access request invalidates `sessionPermissionTag` - and a Download withheld for subject matter
 * enables without a reload (#6992).
 */
function documentTags(documents?: DocumentApiResult[]) {
  return documents
    ? [
        ...documents.map((document) => ({ type: "Document" as const, id: document.id.value })),
        documentListTag,
        sessionPermissionTag,
      ]
    : [documentListTag, sessionPermissionTag];
}

export const documentsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getDocuments: build.query<PaginatedResponse<Document>, DocumentListArgs>({
      query: ({ query }) => ({
        url: "documents",
        params: query.searchParams,
      }),
      rawResponseSchema: z.array(DocumentSchema.DocumentJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: DocumentJSON[], meta): PaginatedResponse<Document> => {
        const paginationHeader = meta?.response?.headers.get("x-pagination") ?? "";
        return PaginatedResponse.from(Document.fromJSONArray(response), paginationHeader);
      },
      providesTags: (result) => documentTags(result?.data),
    }),

    /**
     * The Documents page's counts, its tab badges and every chip's, in one request under the list's
     * own filter (#6710, #6720). `criteria` is that filter as search parameters; the server ignores
     * each chip group's own selection when it counts that group's chips.
     */
    getDocumentListCounts: build.query<DocumentListCounts, { criteria: string }>({
      query: ({ criteria }) => `documents/counts${criteria ? `?${criteria}` : ""}`,
      rawResponseSchema: DocumentListCountsJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: listCountsTags("Document"),
    }),

    getDocumentById: build.query<DocumentApiResult, DocumentIdArgs>({
      query: ({ documentId }) => `documents/${documentId}`,
      rawResponseSchema: DocumentApiResultJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: DocumentApiResultJSON): DocumentApiResult => deserializeDocumentApiResult(response),
      providesTags: (_result, _error, { documentId }) => [
        { type: "Document" as const, id: documentId },
        sessionPermissionTag,
      ],
    }),

    getDocumentsByIds: build.query<DocumentApiResult[], DocumentsByIdsArgs>({
      query: ({ documentIds }) => ({
        url: "documents/get-documents-info-by-ids",
        method: "POST",
        body: documentIds,
      }),
      rawResponseSchema: byIdsResultJSONZ(DocumentSchema.DocumentJSONZ),
      onSchemaFailure: logSchemaFailure,
      // Every id asked about comes back: a document, or an `UnavailableDocument` saying why it is not
      // one, so a caller can tell a refused document from one that is gone.
      transformResponse: (response: ByIdsResultJSON<DocumentJSON>): DocumentApiResult[] => [
        ...response.found.map((document) => Document.fromJSON(document)),
        ...response.unavailable.map(({ id, reason }) =>
          UnavailableDocument.fromJSON({ id, issue: unavailableDocumentIssue[reason] }),
        ),
      ],
      providesTags: documentTags,
    }),

    getDocumentPermissionTargetsByIds: build.query<ByIdsResultJSON<DocumentPermissionTarget>, DocumentsByIdsArgs>({
      query: ({ documentIds }) => ({
        url: "documents/get-permission-targets-by-ids",
        method: "POST",
        body: documentIds,
      }),
      rawResponseSchema: byIdsResultJSONZ(DocumentPermissionTargetJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) =>
        result
          ? result.found.map((document) => ({ type: "Document" as const, id: document.id }))
          : [documentListTag],
    }),

    // Whether this reader's download of each document would be refused, for rows another center
    // projected (#6992). Only the verdicts: a refused or missing id has none, and the server still
    // answers its click.
    getDocumentDownloadVerdictsByIds: build.query<DocumentDownloadVerdict[], DocumentsByIdsArgs>({
      query: ({ documentIds }) => ({
        url: "documents/get-download-verdicts-by-ids",
        method: "POST",
        body: documentIds,
      }),
      rawResponseSchema: byIdsResultJSONZ(DocumentDownloadVerdict.JSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: ByIdsResultJSON<unknown>): DocumentDownloadVerdict[] =>
        response.found.map((verdict) => DocumentDownloadVerdict.fromJSON(verdict)),
      providesTags: (result) =>
        result
          ? [
              ...result.map((verdict) => ({ type: "Document" as const, id: verdict.id.value })),
              documentListTag,
              sessionPermissionTag,
            ]
          : [documentListTag, sessionPermissionTag],
    }),

    downloadDocument: build.query<AxiosResponse<Blob>, DocumentIdArgs>({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/download`,
        responseHandler: async (response: Response) => {
          // A refusal carries the reason it was refused; see `documentDownloadFailureMessage`.
          if (!response.ok) return readDownloadErrorBody(response);

          const headers: Record<string, string> = {};
          response.headers.forEach((value, key) => {
            headers[key] = value;
          });

          return {
            data: await response.blob(),
            headers,
            request: {
              getResponseHeader: (name: string) => response.headers.get(name),
            },
          } as AxiosResponse<Blob>;
        },
      }),
      // No tags, on purpose. A download is an act, not a view of the document: the lazy trigger
      // fetches afresh (and re-checks permission) on every click, and a tag would make every
      // documents-changed or documents-deleted event download the bytes again behind a mounted
      // button - after a delete, as a 404.
      // Dropped as soon as nothing subscribes, for the same reason: the entry holds the file's
      // bytes, and once they are saved nobody reads them again (#5992). A lazy hook still keeps
      // its last download while its component is mounted.
      keepUnusedDataFor: 0,
    }),
    // #endregion

    // #region Mutations
    createDocuments: build.mutation<DocumentApiResult[], { requests: BulkDocumentRequest[] }>({
      async queryFn({ requests }, _api, _extraOptions, fetchWithBQ) {
        const validation = validateDocumentUploadRequests(requests);
        const failuresByRequestId = new Map(
          validation.failures.map(({ request, code, issue }) => [
            request.requestId.value,
            UnavailableDocument.create({
              id: request.requestId,
              status: "refused",
              code,
              issue,
            }),
          ]),
        );

        if (validation.acceptedRequests.length === 0) {
          return {
            data: requests.map((request) => failuresByRequestId.get(request.requestId.value)!),
          };
        }

        const result = await fetchWithBQ({
          url: "documents",
          method: "POST",
          body: new DocumentsFormAPIRequest(validation.acceptedRequests).formData,
        });
        if (result.error) return { error: result.error };

        // Row by row: one answer this bundle cannot read must not cost the others theirs (#6593).
        const serverResults = z.array(z.unknown())
          .parse(result.data)
          .map((row, index) => readUploadAnswer(row, validation.acceptedRequests[index]));
        let serverResultIndex = 0;
        return {
          data: requests.map((request) =>
            failuresByRequestId.get(request.requestId.value) ?? serverResults[serverResultIndex++]
          ),
        };
      },
      invalidatesTags: (result) => {
        const uploadedDocuments = result?.filter((item): item is Document => item instanceof Document) ?? [];
        return uploadedDocuments.length > 0 ? documentTags(uploadedDocuments) : [];
      },
    }),

    updateDocument: build.mutation<
      DocumentApiResult,
      { originalDocument: Document; updatedDocument: Document }
    >({
      async queryFn({ originalDocument, updatedDocument }, _api, _extraOptions, fetchWithBQ) {
        if (!originalDocument.id || !updatedDocument.id) {
          throw new Error("Cannot update document without id.");
        }

        const request = new UpdateDocumentAPIRequest(originalDocument, updatedDocument);
        if (request.payload.length === 0) {
          return { data: updatedDocument };
        }

        const result = await fetchWithBQ({
          url: `documents/${originalDocument.id.value}`,
          method: "PATCH",
          body: request.payload,
        });
        if (result.error) return { error: result.error };

        return {
          data: deserializeDocumentApiResult(DocumentApiResultJSONZ.parse(result.data)),
        };
      },
      invalidatesTags: (_result, _error, { originalDocument }) => [
        { type: "Document" as const, id: originalDocument.id.value },
        documentListTag,
      ],
    }),

    deleteDocument: build.mutation<void, DocumentIdArgs>({
      query: ({ documentId }) => ({
        url: `documents/${documentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, error, { documentId }) => error ? [] : [
        { type: "Document" as const, id: documentId },
        documentListTag,
      ],
    }),

    archiveDocument: build.mutation<void, DocumentIdArgs>({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/archive`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { documentId }) => [
        { type: "Document" as const, id: documentId },
        documentListTag,
      ],
    }),

    /** Saves a chat attachment as a document of the caller's, with these topics (#6053). */
    copyAttachmentToDocuments: build.mutation<DocumentCopyResultJSON, CopyAttachmentArgs>({
      query: ({ forumId, fileId, topics }) => ({
        url: "documents/from-attachment",
        method: "POST",
        body: { forumId, fileId, topics: topics.map((topic) => topic.toJSON()) },
      }),
      rawResponseSchema: DocumentCopyResultJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (result) => (result?.status === "added" ? [documentListTag] : []),
    }),

    /**
     * Replaces a document's subject-matter labels with these, for this reason (#4030). The whole
     * set is sent, not a change to it, and the content hash the labels were read against goes
     * with it: a 409 means the content changed since, and the labels on screen are out of date.
     */
    overrideDocumentClassification: build.mutation<Document, OverrideClassificationArgs>({
      query: ({ documentId, labels, contentHash, reason }) => ({
        url: `documents/${documentId}/classification/override`,
        method: "POST",
        body: { labels, contentHash, reason },
      }),
      rawResponseSchema: DocumentSchema.DocumentJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: DocumentJSON): Document => Document.fromJSON(response),
      // On a refusal too: a 409 means the labels on screen are stale, and a 403 means whether the
      // control should be offered at all has changed. Either way the next read is the truth.
      invalidatesTags: (_result, _error, { documentId }) => [
        { type: "Document" as const, id: documentId },
        documentListTag,
      ],
    }),

    unarchiveDocument: build.mutation<void, DocumentIdArgs>({
      query: ({ documentId }) => ({
        url: `documents/${documentId}/unarchive`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { documentId }) => [
        { type: "Document" as const, id: documentId },
        documentListTag,
      ],
    }),
    // #endregion
  }),
});

export const {
  useGetDocumentsQuery,
  useGetDocumentListCountsQuery,
  useGetDocumentByIdQuery,
  useGetDocumentsByIdsQuery,
  useGetDocumentPermissionTargetsByIdsQuery,
  useGetDocumentDownloadVerdictsByIdsQuery,
  useLazyDownloadDocumentQuery,
  useCreateDocumentsMutation,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,
  useArchiveDocumentMutation,
  useUnarchiveDocumentMutation,
  useCopyAttachmentToDocumentsMutation,
  useOverrideDocumentClassificationMutation,
} = documentsApi;

/** The Documents page's badge and chip counts, one request a page load (#6720). */
export const useDocumentListCounts = listCountsHook<{ criteria: string }, DocumentListCounts>(
  useGetDocumentListCountsQuery,
);
